<template lang="pug">
  modal(:title='$t("inviteUsersModal.title")' @closeModal='closeModal' maxWidth='720px')
    .column.m-top-2.m-bottom-2
      h2.text-big.dark-gray.m-bottom-2 {{ $t('inviteUsersModal.chooseRole') }}
      .row.middle
        label.text-normal.m-right-1 {{ $t('inviteUsersModal.roleLabel') }}
        button.param-button.m-right-1(
          v-for='(roleValue, roleKey) in availableRoles'
          :class='{selected: role === roleValue, bold: role === roleValue}'
          @click='() => changeRole(roleValue)'
        ) {{ $t(`userRoles.${roleKey}`) }}
      .row.top.m-top-1(v-if='showErrors && $v.role.$invalid')
        simple-svg.error-icon.red-icon.m4-right-2.red-stroke-icon(:filepath='errorIcon' :width='"16px"' :height='"16px"')
        span.text-small.red {{ $t('inviteUsersModal.roleRequired') }}
    .column.m-top-2.m-bottom-2(v-if='role === userPrimaryRoles.teacher')
      h2.text-big.dark-gray.m-bottom-2 {{ $t('inviteUsersModal.chooseLevel') }}
      .row.middle
        label.text-normal.m-right-1 {{ $t('inviteUsersModal.levelLabel') }}
        button.param-button.m-right-1(
          v-for='level in availableLevels'
          :class='{selected: levels.includes(level.id), bold: levels.includes(level.id)}'
          @click='toggleLevel(level.id)'
        ) {{ $t(`inviteUsersModal.levels.${level.name.toLowerCase()}`) }}
      .row.top.m-top-1(v-if='showErrors && $v.levels.$invalid')
        simple-svg.error-icon.red-icon.m4-right-2.red-stroke-icon(:filepath='errorIcon' :width='"16px"' :height='"16px"')
        span.text-small.red {{ $t('inviteUsersModal.levelsRequired') }}
    .column.m-top-2.m-bottom-2(v-if='isAdmin && hasKreaLicenseActive')
      h2.text-big.dark-gray.m-bottom-2 {{ $t('inviteUsersModal.chooseSecondaryRole') }}
      .row.middle
        label.text-normal.m-right-1 {{ $t('inviteUsersModal.secondaryRoleLabel') }}
        button.param-button.m-right-1(
          v-for='(roleValue, roleKey) in availableSecondaryRoles'
          :class='{selected: secondaryRole === roleValue, bold: secondaryRole === roleValue}'
          @click='() => changeSecondaryRole(roleValue)'
        ) {{ $t(`userRoles.secondary.${roleKey}`) }}
    .column.m-bottom-1
      tag-selector(
        :label='$t("inviteUsersModal.emailLabel")'
        name='emails' avoidDuplicates
        :placeholder='$t("inviteUsersModal.emailPlaceholder")'
        :tags='emails'
        :updateMethod='updateEmails'
        :maxTags='30'
        :maxLength='50'
        :validTags='emailRx'
        maxHeight='125px'
      )
    .row.top.m-bottom-05(v-if='showErrors && $v.emails.$invalid')
      simple-svg.error-icon.red-icon.m4-right-2.red-stroke-icon(:filepath='errorIcon' :width='"16px"' :height='"16px"')
      span.text-small.red {{ $t('inviteUsersModal.emailsRequired') }}
    .column.m-bottom-2
      .header.row.middle
        h2.item-8.text-normal.bold.white {{ $t('inviteUsersModal.usersList.email') }}
        h2.item-2.text-normal.bold.white {{ $t('inviteUsersModal.usersList.role') }}
        h2.item-3.text-normal.bold.white {{ $t('inviteUsersModal.usersList.secondaryRole') }}
        h2.item-2.text-normal.bold.white {{ $t('inviteUsersModal.usersList.level') }}
      .users.kimple-scroll
        .user.row.middle(v-if='!validEmails.length')
          .item-8.row.middle
            img.avatar.m-right-2(:src='profileDefaultImg')
            h2.text-normal.dark-gray.bold {{ $t('inviteUsersModal.usersList.newUser') }}
          h2.item-2.text-normal.bold.dark-gray {{ role && $t(`userRoles.${role.toLowerCase()}`) }}
          h2.item-3.text-normal.bold.dark-gray {{ secondaryRole && $t(`userRoles.secondary.${secondaryRole.toLowerCase()}`) }}
          .item-2.row.middle
            span.row.middle.center.level.m4-right-1(
              v-for='level in levels'
              :title='$t(`educationalLevels.${getLevel(level)}`)'
            ) {{ getShortLevel(level) }}
        .user.row.middle(v-for='email in validEmails')
          .item-8.row.middle
            img.avatar.m-right-1(:src='profileDefaultImg')
            h2.text-normal.email.bold.dark-gray.ellipsis(:title='email') {{ email }}
          h2.item-2.text-normal.bold.dark-gray {{ $t(`userRoles.${role.toLowerCase()}`) }}
          h2.item-3.text-normal.bold.dark-gray {{ secondaryRole && $t(`userRoles.secondary.${secondaryRole.toLowerCase()}`) }}
          .item-2.row.middle
            span.row.middle.center.level.m4-right-1(
              v-for='level in sortedLevels'
              :title='$t(`educationalLevels.${getLevel(level)}`)'
            ) {{ getShortLevel(level) }}
    button.primary-button.with-icon(@click='submit')
      simple-svg.m-right-1.white-icon(:filepath='sendIcon' width='20px' height='20px')
      | {{ $t('inviteUsersModal.sendInvitations') }}
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { rolesIds, userPrimaryRoles, userSecondaryRoles } from '../../utils/constants'
import profileDefaultImg from '@/assets/profile_default_img.png'
import Modal from '../Modal'
import TextInput from '@/components/Inputs/TextInput'
import TagSelector from '@/components/Inputs/TagSelector'
import crossIcon from '@/assets/ic_cross.svg'
import { emailRx } from '../../utils/validators'
import sendIcon from '@/assets/ic_send.svg'
import userRole from '../../mixins/userRole'
import { toLowerCamelCase } from '../../utils/string'
import { i18n } from '../../main'
import kreaRoles from '../../mixins/kreaRoles'
import errorIcon from '@/assets/ic_stop.svg'
import { required, minLength } from 'vuelidate/lib/validators'

export default {
  mixins: [userRole, kreaRoles],
  components: { Modal, TextInput, TagSelector },
  data() {
    return {
      sendIcon,
      errorIcon,
      emailRx,
      crossIcon,
      profileDefaultImg,
      rolesIds,
      userPrimaryRoles,
      showErrors: false,
      role: null,
      secondaryRole: null,
      levels: [],
      emails: []
    }
  },
  watch: {
    role() {
      this.levels = []
    }
  },
  async created() {
    await this.hydrateProfile()
    await this.hydrateSchoolInfo(this.$route.params.schoolId)
    if (this.isManager) {
      this.role = userPrimaryRoles.teacher
    }
    if (this.currentSchool.impartedLevels.length === 1) {
      this.level = this.currentSchool.impartedLevels
    }
  },
  computed: {
    ...mapState({
      currentSchool: state => state.profile.userProfile.schoolGroupVO.currentSchool,
      educationalLevels: state => state.profile.educationalLevels
    }),
    sortedLevels() {
      return this.levels
        .map(lvl => lvl)
        .sort((a, b) => a - b)
    },
    validEmails() {
      return this.emails.filter(email => emailRx.test(email))
    },
    disableSubmit() {
      return (!this.role || (this.role === rolesIds.teacher && !this.levels.length)) || !this.emails.length || this.emails.some(email => !emailRx.test(email))
    },
    availableLevels() {
      return this.educationalLevels.filter(level => this.currentSchool.impartedLevels.includes(level.id))
    },
    availableRoles() {
      const roles = { ...userPrimaryRoles }
      delete roles.trial
      delete roles.kimple
      delete roles.admin
      if (!this.isAdmin) delete roles.manager
      return roles
    },
    availableSecondaryRoles() {
      const roles = { ...userSecondaryRoles }
      delete roles.approvingAuthor
      return roles
    }
  },
  methods: {
    ...mapActions([
      'showElement',
      'hideElement',
      'hydrateProfile',
      'hydrateSchoolInfo',
      'sendInvitations'
    ]),
    closeModal() {
      this.hideElement({ element: 'inviteUsersModal' })
    },
    changeRole(role) {
      this.role = role
    },
    changeSecondaryRole(role) {
      if (this.secondaryRole === role) {
        this.secondaryRole = null
      } else {
        this.secondaryRole = role
      }
    },
    toggleLevel(levelId) {
      if (this.levels.includes(levelId)) {
        this.levels = this.levels.filter(lvl => lvl !== levelId)
      } else {
        this.levels = [...this.levels, levelId]
      }
    },
    getShortLevel(level) {
      return i18n.t(`educationalLevels.${this.getLevel(level)}`).slice(0, 3) + '.'
    },
    updateEmails(newValue) {
      this.emails = newValue
    },
    async submit() {
      this.showErrors = false
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.showErrors = true
        return
      }
      this.showElement({ element: 'fullPageLoader' })
      const success = await this.sendInvitations({
        emails: this.emails,
        roleName: this.role && toLowerCamelCase(this.role),
        secondaryRoleName: this.secondaryRole && toLowerCamelCase(this.secondaryRole),
        schoolId: this.currentSchool.id,
        educationalLevels: this.levels
      })
      if (success) {
        this.hideElement({ element: 'inviteUsersModal' })
        this.showElement({ element: 'sentInvitationsModal' })
        this.$emit('invitedUsers')
      }
      this.hideElement({ element: 'fullPageLoader' })
    }
  },
  validations() {
    return ({
      emails: { required, minLength: minLength(1) },
      role: { required },
      levels: this.role === userPrimaryRoles.manager ? {} : { required }
    })
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';
@import 'src/scss/common/svg';

.param-button{
  padding: 8px 20px;
  border-radius: 5px;
  border: 1px solid $gray;
  min-width: 120px;
  &.selected{
    background-color: $yellow;
    color: $black;
  }
}

.header{
  box-shadow: 0px 5px 10px -1px $shadow-gray2;
  background-color: $blue;
  border-radius: 5px;
  padding: 5px 15px;
}

.emails{
  max-height: 130px;
  overflow-y: auto;
  flex-wrap: wrap;
  .email{
    background-color: $light-gray;
    border-radius: 5px;
    padding: 5px 10px;
    margin: 5px;
    &.error{
      background-color: $red;
      color: $white;
    }
  }
}

.users{
  height: 220px;
  overflow-y: auto;
  &>:nth-child(odd){
    background-color: $light-gray;
  }
  &>:nth-child(even){
    background-color: $white;
  }
  .user{
    padding: 10px 15px;
    .avatar{
      width: 32px;
      height: 32px;
    }
    .email{
      max-width: 265px;
    }
  }
}

.level{
  border-radius: 50%;
  border: 1px solid $dark-gray;
  width: 32px;
  height: 32px;
}

</style>
